/**
 * stats-panel-enhance.js — the stats-v2 run panel fragment's triad half
 * (`templates/stats-panel.html`, stats-panel-v2-triad-redesign design): maps every
 * `data-stat-icon="<token>"` placeholder span to its icon factory (the `games-list-enhance.js`
 * token->factory pattern), mounts the diverging-bars + donut charts off the fragment's
 * `sp-chart-data` spans (design § The shared dimension), and wires the hover/pin cross-highlight
 * between the two charts and the fixed-slot context block (design § Interaction contract).
 * Show-don't-tell: the fragment ships no words, so every slot this module fails to fill stays
 * invisible, not broken — each wrapper's `data-tooltip`/`aria-label` (server-rendered, i18n'd)
 * carries the accessible name regardless.
 *
 * The fragment's OTHER client half is the sibling `stats-panel-net-chart.js`
 * (leaderboard-row-daily-net-columns): the net chart off the `sp-net-data` side channel, carried
 * by the expanded leaderboard row and — since stats-panel-unification slice 2 — by `/games` too,
 * and absent whenever the server sent no series (that file's header enumerates the cases).
 * `enhance` calls both, and why it is a sibling module rather than a mount in here is on that
 * file's own header.
 *
 * No self-wiring (the `round-inspection-enhance.js` DOMContentLoaded lesson, design § Structure &
 * SRP): both fragment consumers — `leaderboard/leaderboard-expansion.js`'s lazy row expansion and
 * `games-list-enhance.js` for the `/games` panel that absorbed the same triad — mount the fragment
 * into an already-loaded page, so each imports `enhance` and calls it directly after mounting.
 *
 * `enhance` is async because the charts module loads on demand (see loadCharts below — this module
 * is eager, charts.js must not be). Icons and interaction are wired synchronously; only the chart
 * mount awaits, so a caller that measures the fragment's settled height must await the returned
 * promise.
 */
import { SUIT_COLORS, createSuitPipSvg } from "./cards/cards.js";
import {
  createChalice,
  createFallenChalice,
  createNullZero,
} from "./icons/game-type-icons.js";
import {
  createBattleStandardPair,
  createGatehouseClaimed,
} from "./icons/role-icons.js";
import {
  createOutcomeStandardUp,
  createOutcomeStandardDown,
} from "./icons/outcome-icons.js";
import { fillKpiIcons } from "./stats-kpi-icons.js";
import {
  createCross,
  createOuvertFan,
  createScissorsClosed,
  createScissorsOpen,
  createSkatCardsStruck,
  createCountingFrame,
} from "./icons/prompt-icons.js";
import {
  createBalanceScale,
  createBidWeight,
  createWorthChest,
  createAugenTallyArcs,
} from "./icons/stat-icons.js";
import {
  createPersonaKnight,
  createPersonaKing,
  createPersonaPrince,
} from "./icons/persona-icons.js";
import { IDENTITY_COLOR, NEUTRAL_CLOTH } from "./constants.js";
import { t } from "./i18n.js";
import { exactDetail } from "./compact-numbers.js";
import { mountNetChart } from "./stats-panel-net-chart.js";

// charts/charts.js is deliberately NOT a static import. Both consumers import this module
// statically, which puts it on the eager boot graph — and a static edge from here would drag
// charts.js + chart-math.js (~40 KB) onto the game page's boot preload for a surface that renders
// only after a leaderboard-row click or a Stats-overlay open. docs/PERFORMANCE.md: "A surface
// needed minutes after boot doesn't ride the boot graph" (the result surface and reference-overlay
// take the same dynamic seam). Memoized, so a re-enhance resolves the already-loaded module.
let chartsPromise = null;
function loadCharts() {
  chartsPromise ??= import("./charts/charts.js");
  return chartsPromise;
}

// Body tokens carry NO size here: `.sp-icon > svg` fills its host (skat.css § Stats Panel), which
// rides --sp-icon-unit, so a body glyph follows that unit wherever it resolves — panel, narrow-
// container step-down, or the context block's continuous --sp-block-unit — with nothing to keep in
// sync. A mirrored 28px literal used to live here and desynced the moment the box shrank below it.
// The larger section/title markers are the exception: they size from HEADER_SIZE below, calibrated
// per token against measured ink, and carry no CSS box at all (.sp-title-icon). They still follow
// the block's own unit — sizeFor scales every one of them by blockScale below, so the whole title
// row shrinks with the grid it heads rather than standing absolute beside it.
const HEADER_UNIT = 44; // fallback header size for any marker not in HEADER_SIZE below

// Per-token box sizes for the context block's title markers (and the bars gutter's mass-calibrated
// icon pairs, see gutterIconSize below). why: stats-v2 revisit, Andre's "game-type icons aren't the
// same height" call. Unchanged by the triad redesign — the matrix axis markers these once also
// sized are gone (retired with the matrix), but every token here still earns its keep via the
// context-block title row (sizeFor below) and the bars gutter's mass calibration (gutterIconSize).
// The calibration target has moved twice since that original call, each time driven by Andre's own
// live acceptance read rather than a re-derived abstract figure — this paragraph states the CURRENT
// rule; the two superseded ones are recorded at their own table entries below so a future retune
// doesn't silently re-walk either dead end.
//
// Current rule: ink AREA (perceived visual mass), not ink height. Two prior rounds targeted ink
// HEIGHT — first a shared ~40px figure (this comment's own original framing, now superseded), then
// the suit pips' own measured height directly (per-suit-farbspiel-entries-gutter-icon-sizing-
// tweaks, "the suit pips are great... use them as the goal reference") — both left chalice ("Grand")
// reading small even once its ink height matched the target. Root cause: height alone is a proxy
// for perceived mass that only holds when glyphs share a comparable aspect ratio, and chalice draws
// unusually tall-and-narrow. Measured live (compact iconPx 18, the union of every drawn shape
// element's own getBoundingClientRect — stroke-inclusive, a variant of the ink-height round's
// getBBox()+getScreenCTM() technique that also counts stroke width; the two corroborate where they
// overlap, e.g. clubs/diamonds' heights reproduce byte-for-byte): diamonds 12.22×14.67 (area 179,
// aspect 0.83), hearts 14.67×15.89 (233, 0.92), spades 13.44×17.11 (230, 0.79), clubs 12.22×17.72
// (217, 0.69), null-zero 12.60×16.80 (212, 0.75) — a 179–233 accepted ink-area band, aspect ratios
// 0.69–0.92. Chalice at its height-matched size (HEADER_SIZE 40): 9.02×15.68 (area 141, aspect
// 0.58) — height on target, area 21% under the band's own floor (179) and 32% under its midpoint
// (206), aspect the outlier by a wide margin. An earlier, now-superseded round already sensed this
// without naming it: its own comment (since rewritten) justified pulling null-zero and fallen-
// chalice's HEADER_SIZE below the ~40px height target because "filled shapes read heavier than
// their ink-height alone suggests" — mass was always the real target, height only ever stood in for
// it, correctly for every token here except chalice. A future retune that "fixes" chalice back down
// to height parity with the suit pips would be reintroducing this exact bug — check its ink AREA
// against the suit pips' own band first, not its height.
const HEADER_SIZE = {
  "gatehouse-claimed": 50,
  "battle-standard-pair": 61,
  // chalice: height-matched to 40 read small (area 141 against the suits' 179-233 band, aspect
  // 0.58 — the outlier, per this section's own comment). Solved for ink AREA instead: target the
  // band's own midpoint (179+233)/2 = 206, scale = sqrt(206/141) = 1.209, 40 * 1.209 = 48.3 -> 48.
  // Verified live post-fix, not just predicted: measured ink area 221 (between clubs' 217 and
  // hearts' 233 — squarely inside the band; the model predicted 141*(48/40)^2 = 203, real-world
  // ~9% above it, plausibly measurement noise on a small glyph rather than a modeling error worth
  // chasing). Chalice's ink height ends up taller than the suit pips' own after this (a narrow
  // glyph needs more height to carry equal area) — that's correct, not a regression; don't pull it
  // back to height parity, and don't "correct" 48 down toward the 206 target either — 221 is the
  // number that was actually measured and accepted.
  chalice: 48,
  // null-zero measured at 11.2px ink height (old HEADER_SIZE 35) — the largest shortfall against
  // the suit-pip target, needing 35->51. Its aspect (0.75) sits inside the suits' own band, so
  // height parity was the right fix here — accepted by Andre, unchanged since.
  "null-zero": 51,
  // fallen-chalice measured at 12.33px ink height (old HEADER_SIZE 42) — needing 42->56. Its ink's
  // own left edge sits flush against its box's left edge (near-zero inset) where every other token
  // here — including the suit pips — carries a ~20% inset from centered composition; matching ink
  // HEIGHT alone doesn't reach for that, so Ramsch's icon read slightly left of the others even at
  // the right size — a separate axis this table alone doesn't cover, closed by INK_OFFSET_X below.
  // Accepted by Andre, unchanged since.
  "fallen-chalice": 56,
  // Suit pips (per-suit-farbspiel-entries, recalibrated per-suit-farbspiel-entries-gutter-icon-
  // sizing-tweaks): the acceptance-round reference every other token above now targets. Previously
  // absent, so gutterIconSize/sizeFor fell back to HEADER_UNIT (44) — no calibration bump at all,
  // reading undersized beside the role icons. One shared value across all four suits rather than
  // four individually fitted ones: their own ink-height varies suit to suit (clubs' clover reads
  // tallest, diamonds' point shortest — real silhouette variation, not a bug, the same way no two
  // suit pips on a real card fill identical ink), but that spread is the ordinary suit-to-suit
  // variation within one glyph family, not the cross-family mismatch this table corrects for.
  // Andre accepted this value outright — do not re-derive it against anything else in this table.
  "suit-pip-C": 54,
  "suit-pip-D": 54,
  "suit-pip-H": 54,
  "suit-pip-S": 54,
};

function plain(factory) {
  return (size) => factory(size);
}

function suitPip(suit) {
  return (size) => createSuitPipSvg(suit, size, SUIT_COLORS[suit]);
}

function persona(factory, identityColor) {
  return (size) => factory(size, identityColor);
}

// The panel's own base --sp-icon-unit (skat.css § Stats Panel). HEADER_SIZE's boxes were
// calibrated against a block rendering at this unit, so it is what the mount's resolved unit is
// measured against below.
const BLOCK_UNIT_BASE = 28;

// The context block's resolved sizing unit at this mount, as a factor of the base. Read off a body
// icon's rendered width rather than off --sp-block-unit itself: that property is unregistered and
// holds `min(var(--sp-icon-unit), calc(25cqi / var(--sp-block-ratio)))`, so getPropertyValue hands
// back the substituted-but-unresolved token stream, not a number. `.sp-context-grid` rebinds
// --sp-icon-unit to --sp-block-unit and `.sp-icon` is `width: var(--sp-icon-unit)`, so a body
// icon's width IS the unit in px — bar the chalice host, whose own rule bumps it 1.25x.
// Falls back to 1 when the panel isn't laid out yet (or carries no grid at all). Be honest about
// what that costs rather than calling it graceful: scale 1 is the PRE-FIX rendering, so on a
// narrow mount it puts a 61px marker back above a ~36px --sp-title-unit reserve — the overflow
// this function exists to remove. It is the right fallback anyway (a wrong scale beats a NaN size,
// and the mount is fixable by re-running fillIcons), and no live mount reaches it: all three call
// paths lay the panel out before enhance() — the leaderboard detail is `max-height: 0;
// overflow: hidden`, which still lays out, and the result overlay and /games mount visible.
// The guard earns its keep against a future mount that enhances a detached or display:none
// fragment; if one ever appears, the fix is to measure after attach, not to widen this branch.
function blockScale(root) {
  const icon = root.querySelector(
    '.sp-context-variant:not([hidden]) .sp-context-grid .sp-icon:not([data-stat-icon="chalice"])',
  );
  const px = icon == null ? NaN : parseFloat(getComputedStyle(icon).width);
  return px > 0 ? px / BLOCK_UNIT_BASE : 1;
}

// Title markers (the context block's .sp-context-title icons — the group/category identity above
// the fixed grid) render at their calibrated HEADER_SIZE times the mount's block scale. The
// calibration is the ink RELATIONSHIP between the tokens, not an absolute pixel figure — one
// factor for every marker, so the equal-ink grammar survives the scaling, and skat.css's
// --sp-title-unit reserves the row on the same ratio. Absolute is what it used to be, and on a
// 317px phone mount that left a 54px marker heading 16.5px body icons.
// Every other slot draws sizeless — `null` leaves the SVG without width/height attributes, so the
// CSS host owns the rendered size (see this file's header comment; the chalice's body bump lives
// on that host too). The matrix's two-header axis-marker grammar (.sp-axis-col/-row, .sp-cell-mark)
// retired with the triad redesign — the context block's title row is its one surviving
// header-scale consumer.
function sizeFor(slot, scale) {
  if (slot.closest(".sp-context-title") == null) return null;
  return (HEADER_SIZE[slot.dataset.statIcon] ?? HEADER_UNIT) * scale;
}

// The fragment's exact token vocabulary (stats-panel.html's data-stat-icon spans) — no more, no
// less; stats-panel-enhance.test.js's own "no unused map entry" half scrapes only the
// template + StatsPanelBlockGrammar/StatsPanelSlots (allTokens()), so it CAN'T see
// stats-band-matrix.js's separate, direct import of a subset of these factories (suit pips,
// chalice, null-zero, standard-up, gatehouse-claimed, battle-standard-pair, and the feat tokens —
// the stats-band matrix's own shared grammar, extracted off result-stats-band.js and now also the
// leaderboard row expansion's mount). If the fragment ever stops referencing a token the matrix
// still calls, that test correctly goes red (an "unused" key) — but its obvious-looking fix,
// deleting the key, is exactly what breaks the matrix at runtime (ICON_FACTORIES[token]
// undefined): the red signal is real, the natural repair isn't.
// Check both consumers before removing a key on that test's say-so. The
// declarer marker is the family's claimed gatehouse (role-identity.js#getRoleIcon: declarer ->
// gatehouse, defender -> banners), rendered vellum (NEUTRAL_CLOTH — the palette's section-marker
// cloth) like every other role marker here.
export const ICON_FACTORIES = {
  arcs: plain(createAugenTallyArcs),
  "battle-standard-pair": plain(createBattleStandardPair),
  "bid-weight": plain(createBidWeight),
  chalice: plain(createChalice),
  chest: plain(createWorthChest),
  "counting-frame": plain(createCountingFrame),
  cross: plain(createCross),
  "fallen-chalice": plain(createFallenChalice),
  fan: plain(createOuvertFan),
  "gatehouse-claimed": (size) => createGatehouseClaimed(size, NEUTRAL_CLOTH),
  "null-zero": plain(createNullZero),
  scale: plain(createBalanceScale),
  "scissors-closed": plain(createScissorsClosed),
  "scissors-open": plain(createScissorsOpen),
  "standard-down": plain(createOutcomeStandardDown),
  "standard-up": plain(createOutcomeStandardUp),
  "struck-skat": plain(createSkatCardsStruck),
  "suit-pip-C": suitPip("clubs"),
  "suit-pip-D": suitPip("diamonds"),
  "suit-pip-H": suitPip("hearts"),
  "suit-pip-S": suitPip("spades"),
  // Standings row personas (template line ~130): dynamic per-seat tokens, each pinned to the
  // court's fixed identity color (design § KPI catalog, Standings row — "identity-colored").
  "persona-knight": persona(createPersonaKnight, IDENTITY_COLOR.human),
  "persona-king": persona(createPersonaKing, IDENTITY_COLOR.left),
  "persona-prince": persona(createPersonaPrince, IDENTITY_COLOR.right),
};

/** Fills every `[data-stat-icon]` slot under `root` with its icon, baking a pixel size only into
 *  the calibrated title markers (sizeFor). Idempotent (skips an already-filled slot) — safe to
 *  call again on a fragment re-fetch. An unmapped token or a slot filled elsewhere is left alone,
 *  matching every other family enhancer's tolerance.
 *
 *  The block scale is read once, at enhance time, and baked in — so a RESIZE after mount leaves
 *  the title markers at their mount-time scale while the CSS-sized body icons around them reflow.
 *  Accepted, not overlooked: HEADER_SIZE already behaved exactly this way (it just never scaled at
 *  all), the panel's two mounts are an overlay and a row expansion that are opened and closed
 *  rather than resized, and a ResizeObserver for a case nobody hits is the flexibility CODING's
 *  YAGNI rule asks us not to build. */
function fillIcons(root) {
  const scale = blockScale(root);
  for (const slot of root.querySelectorAll("[data-stat-icon]")) {
    if (slot.firstChild != null) continue;
    const factory = ICON_FACTORIES[slot.dataset.statIcon];
    if (factory == null) continue;
    slot.appendChild(factory(sizeFor(slot, scale)));
  }
}

// --- Charts (design § The shared dimension) ---------------------------------------------------

// Fixed group order (per-suit-farbspiel-entries — retired the old 4-group "farbspiel" bucket):
// exactly ROUND_TYPE_ORDER, lowercased — diamonds, hearts, spades, clubs, grand, null, ramsch. A
// group now IS a type: the donut's dimension collapsed onto charts.js's own round-type order, so
// the "grouped donut dimension" deviation charts/CLAUDE.md once recorded no longer exists.
const GROUP_ORDER = [
  "diamonds",
  "hearts",
  "spades",
  "clubs",
  "grand",
  "null",
  "ramsch",
];

// A quadrant category's type half ("clubs-declarer" -> "clubs") maps to the same i18n key as its
// group label — one shared table, since a suit/Grand type and its donut group are now the same
// concept (unlike the old farbspiel grouping, where four types folded into one group with its own
// name). Null/Ramsch have no role suffix — they're their own group already.
const TYPE_LABEL_KEY = {
  clubs: "clubs",
  spades: "spades",
  hearts: "hearts",
  diamonds: "diamonds",
  grand: "gameTypeGrand",
  null: "gameTypeNull",
  ramsch: "ramsch",
};
const ROLE_LABEL_KEY = {
  declarer: "resultDeclarer",
  defender: "leaderboardStatGegenspieler",
};

function groupLabel(key) {
  return t(TYPE_LABEL_KEY[key]);
}

// A category's bars-row label: a quadrant category's key is "<type>-<role>" — compose "type ·
// role" from two existing i18n keys (a UI-opinion composition, not a game fact — CODING's
// server-vs-client split); ramsch carries no role suffix, so it's just its own label. Null joined
// the role-split quadrant categories (stats-panel-per-suit-farbspiel-entries-role-split) — its key
// is now "null-declarer"/"null-defender" like every other type, so it falls through to the generic
// split below rather than needing its own branch here.
function categoryLabel(key) {
  if (key === "ramsch") return groupLabel(key);
  const [type, role] = key.split("-");
  return `${t(TYPE_LABEL_KEY[type])} · ${t(ROLE_LABEL_KEY[role])}`;
}

const TYPE_ICON_TOKEN = {
  clubs: "suit-pip-C",
  spades: "suit-pip-S",
  hearts: "suit-pip-H",
  diamonds: "suit-pip-D",
  grand: "chalice",
  null: "null-zero",
};
const ROLE_ICON_TOKEN = {
  declarer: "gatehouse-claimed",
  defender: "battle-standard-pair",
};
// Ramsch stays the only solo (roleless) category — Null joined the role-split quadrant categories
// (stats-panel-per-suit-farbspiel-entries-role-split), so its type+role icon pair now composes
// through the generic path below via TYPE_ICON_TOKEN.null, same as every other type.
const SOLO_CATEGORY_TYPE_TOKEN = { ramsch: "fallen-chalice" };

// A quadrant category's bars-gutter icon pair: the role mark beside the type's own pip/chalice —
// role first, then game type (Andre's driven verdict, stats-panel-per-suit-farbspiel-entries,
// swapped from the type-then-role order the triad-redesign design originally specified) — derived
// from the key's "<type>-<role>" shape (mirrors categoryLabel above) rather than a hand-maintained
// 10-entry table, now that a type IS a group (per-suit-farbspiel-entries). `roleToken` is `null`
// for Ramsch — categoryIcon still reserves that slot's width for it (see roleSlotPx below), so its
// lone type icon lines up with every other row's.
function categoryIconParts(key) {
  if (SOLO_CATEGORY_TYPE_TOKEN[key] != null) {
    return { roleToken: null, typeToken: SOLO_CATEGORY_TYPE_TOKEN[key] };
  }
  const [type, role] = key.split("-");
  return {
    roleToken: ROLE_ICON_TOKEN[role],
    typeToken: TYPE_ICON_TOKEN[type],
  };
}

// The bars gutter's mass-calibrated icon size: reuses HEADER_SIZE's ink-to-box factors (the panel
// already solved "a fallen chalice reads heavier than a null-zero at equal box size" once, at
// header scale) normalized against HEADER_UNIT (the calibration's own neutral baseline) and
// reapplied at the gutter's own iconPx unit — pixel-checked in the driving pass per the design.
function gutterIconSize(token, iconPx) {
  return Math.round(
    iconPx * ((HEADER_SIZE[token] ?? HEADER_UNIT) / HEADER_UNIT),
  );
}

// The role slot's fixed width (Andre's driven verdict, per-suit-farbspiel-entries — "reserve the
// same width so the game-type icons are left aligned with each other"): gatehouse-claimed and
// battle-standard-pair calibrate to different gutter sizes (HEADER_SIZE 50 vs 61), so an
// icon-sized (rather than fixed-width) slot starts each row's type icon at a different x depending
// on which role icon precedes it — or, for Ramsch, no icon at all. Sized to the wider of the two
// role icons at the row's own gutter scale; categoryIcon reserves the identical width for Ramsch's
// empty spacer.
function roleSlotPx(iconPx) {
  return Math.max(
    ...Object.values(ROLE_ICON_TOKEN).map((token) =>
      gutterIconSize(token, iconPx),
    ),
  );
}

// Every type token categoryIcon's typeHost can draw — TYPE_ICON_TOKEN's six types plus Ramsch's
// solo fallen-chalice (SOLO_CATEGORY_TYPE_TOKEN) — the vocabulary iconOnlyGutterPx below sizes
// against. Computed from the two source tables rather than hand-picked (e.g. "suit-pip-C is the
// worst case today"), so a future HEADER_SIZE calibration change can't silently reopen the
// clipping bug this closes.
const ALL_TYPE_TOKENS = [
  ...Object.values(TYPE_ICON_TOKEN),
  ...Object.values(SOLO_CATEGORY_TYPE_TOKEN),
];

// The gap between categoryIcon's own role/type icon pair — shared with iconOnlyGutterPx below so
// the two can't drift independently (that computation's own budget depends on this exact value).
const CATEGORY_ICON_GAP_PX = 4;

// The widest type icon BOX this vocabulary can draw at a given iconPx — the height budget
// (iconOnlyIconHeightPx below) uses this figure directly, since a token's box height is exactly
// what needs to fit. The width budget (iconOnlyGutterPx below) needs a different per-token max —
// box PLUS any INK_OFFSET_X nudge, since that horizontal margin is real occupied width but adds no
// height — so it re-runs its own ALL_TYPE_TOKENS reduction rather than calling this function; the
// two can't drift apart from EACH OTHER even so, since both draw from the same underlying
// gutterIconSize/ALL_TYPE_TOKENS primitives, just combined differently per axis.
function maxTypeIconPx(iconPx) {
  return Math.max(
    ...ALL_TYPE_TOKENS.map((token) => gutterIconSize(token, iconPx)),
  );
}

// fallen-chalice's own drawn ink sits flush against its box's left edge — a deliberately
// off-balance "tilted" composition (the glyph's own name), unlike every other type icon here,
// whose ink centers with a consistent ~20% left inset. Scaling its box to match the suit pips' ink
// HEIGHT (HEADER_SIZE's own comment) doesn't reach this: a proportional scale-up preserves the
// flush composition, it doesn't add the missing inset — confirmed live (Andre's acceptance-round
// "not quite on the same X" report): after the height retune, Grand and Null's ink converged on
// the suit pips' own x within ~1px (a byproduct of their own ink already carrying a similar
// proportional inset), fallen-chalice's stayed 0.95px LEFT of its box edge against the suit pips'
// own ~4.43px average inset. A small measured left margin on this one token's icon host closes
// that gap directly — scoped to categoryIcon's own gutter composition (typeHost's margin below),
// so it can't touch the shared factory other surfaces (the context-block title row, Ramsch's own
// feat slot) also draw from, whichever role/type pairing a token ends up in. Every other token is
// flush (0) — this table stays open to a second entry, not pre-built for one.
const INK_OFFSET_X = { "fallen-chalice": 13 }; // HEADER_UNIT-scale, ~5px at compact (iconPx 18)

function inkOffsetX(token, iconPx) {
  return Math.round((iconPx * (INK_OFFSET_X[token] ?? 0)) / HEADER_UNIT);
}

// buildDivergingBars' iconOnly gutter opts.iconOnlyGutterPx hook (charts/charts.js fix round,
// stats-panel-per-suit-farbspiel-entries): charts.js's generic `2·iconPx + 8` default assumes a
// roughly iconPx-sized single icon, but categoryIcon composes a mass-calibrated role+type pair
// that's deliberately larger than iconPx on both halves (gutterIconSize, roleSlotPx) — the pair
// routinely exceeded that default and got clipped by the gutter's own overflow:hidden. The true
// required width is the same three pieces categoryIcon itself lays out left-to-right: the fixed
// role slot, the pair's own gap, and the widest type icon this vocabulary can draw at this iconPx
// (INK_OFFSET_X's own extra nudge included, since that's real occupied width too) — computed
// generically across ALL_TYPE_TOKENS rather than assuming today's worst case stays the worst case.
function iconOnlyGutterPx(iconPx) {
  const maxTypeContentPx = Math.max(
    ...ALL_TYPE_TOKENS.map(
      (token) => gutterIconSize(token, iconPx) + inkOffsetX(token, iconPx),
    ),
  );
  return roleSlotPx(iconPx) + CATEGORY_ICON_GAP_PX + maxTypeContentPx;
}

// buildDivergingBars' iconOnly gutter opts.iconOnlyIconHeightPx hook — the same clipping bug as
// iconOnlyGutterPx above, on the other axis: charts.js's gutter fixes its icon host's height at
// the generic iconPx too, and the gutter's own box (overflow:hidden) auto-sizes to that same
// fixed height regardless of what the caller's icon(key, size) factory actually draws inside it —
// a role or type icon calibrated taller than iconPx (e.g. battle-standard-pair, suit pips) had its
// top/bottom shaved by a fraction of a pixel. Unlike the width budget, role and type sit side by
// side rather than summed, so the required height is just the taller of the two, not their total —
// reusing roleSlotPx/maxTypeIconPx's own WIDTH numbers as a height budget relies on every factory
// this vocabulary draws returning a square size×size box (true today: every icons/*.js factory
// takes one `size` param for both dimensions of its viewBox). A future factory that draws taller
// than it is wide would reopen this exact clip with no signal here — the square-box assumption
// isn't asserted anywhere, only relied on.
function iconOnlyIconHeightPx(iconPx) {
  return Math.max(roleSlotPx(iconPx), maxTypeIconPx(iconPx));
}

/** The bars gutter's icon(key, size) factory (charts.js's buildDivergingBars contract): composes
 *  the panel's identity pair — the claimed-gatehouse/battle-standard-pair role mark (or Ramsch's
 *  same-width empty spacer) in a fixed-width slot, beside a suit pip/chalice/null-zero/fallen-
 *  chalice type icon sized by gutterIconSize — left-aligned in the iconOnly gutter's full width
 *  (charts.js's own contract for that opt), with every row's type icon *box* starting at the same
 *  x regardless of the role slot's content (roleSlotPx's own comment) — and, for a token whose
 *  INK doesn't fill its box the way the others' does, a small extra left margin (inkOffsetX) so
 *  the visible ink itself lines up too, not just the invisible box (fallen-chalice/Ramsch today,
 *  INK_OFFSET_X's own comment). */
function categoryIcon(key, iconPx) {
  const host = document.createElement("div");
  host.style.cssText = `display:flex; align-items:center; gap:${CATEGORY_ICON_GAP_PX}px;`;

  const { roleToken, typeToken } = categoryIconParts(key);
  const roleHost = document.createElement("div");
  // The role marker stays neutral regardless of the row's own ink — the game-type mark beside it
  // needs no such pin, every game-type factory paints an explicit fill already immune to the
  // ambient color (inkVarFor, per-suit-farbspiel-entries-role-split). Set unconditionally, even for
  // Ramsch's empty slot — a color on a childless host is inert.
  roleHost.style.cssText = `display:flex; align-items:center; justify-content:center; flex:none; width:${roleSlotPx(iconPx)}px; color:${NEUTRAL_CLOTH};`;
  if (roleToken != null) {
    roleHost.appendChild(
      ICON_FACTORIES[roleToken](gutterIconSize(roleToken, iconPx)),
    );
  }
  host.appendChild(roleHost);

  const typeHost = document.createElement("div");
  typeHost.style.cssText = `display:flex; flex:none; margin-left:${inkOffsetX(typeToken, iconPx)}px;`;
  typeHost.appendChild(
    ICON_FACTORIES[typeToken](gutterIconSize(typeToken, iconPx)),
  );
  host.appendChild(typeHost);

  return host;
}

/** Reads the fragment's explicit backend data (design § Structure — "the client never parses
 *  rendered text"): one `{key, group, rounds, total}` entry per unlocked category, in server
 *  order. */
function readCategories(panel) {
  return [...panel.querySelectorAll(".sp-chart-data")].map((span) => ({
    key: span.dataset.key,
    group: span.dataset.group,
    rounds: Number(span.dataset.rounds),
    total: Number(span.dataset.total),
  }));
}

// Groups fold client-side from the category spans' data-group (design § Structure — "mirroring
// the server fold"): sum each group's rounds over GROUP_ORDER, dropping an empty group entirely
// (progressive unlock — a 0-round group emits no donut segment).
function foldGroups(categories) {
  const rounds = Object.fromEntries(GROUP_ORDER.map((key) => [key, 0]));
  for (const cat of categories) rounds[cat.group] += cat.rounds;
  return GROUP_ORDER.filter((key) => rounds[key] > 0).map((key) => ({
    key,
    label: groupLabel(key),
    value: rounds[key],
  }));
}

// Shared by both charts (stats-panel-per-suit-farbspiel-entries-role-split extended the donut's
// own map to the bars too): a group key is exactly a round-type key, so this is the one ink table
// for both the donut's per-segment paint and the bars' per-row paint below.
const GROUP_INK_VAR = {
  clubs: "--chart-clubs",
  spades: "--chart-spades",
  hearts: "--chart-hearts",
  diamonds: "--chart-diamonds",
  grand: "--chart-grand",
  null: "--chart-null",
  ramsch: "--chart-ramsch",
};

// The bars now paint by game type, not by sign (stats-panel-per-suit-farbspiel-entries-role-split,
// js/charts/CLAUDE.md § Vocabulary revision: per-category ink on a palette-owning dimension — the
// stats-v2 panel's own type axis owns a palette the same way /games' by-round-type net chart does):
// both role halves of a type share that type's ink, resolved per bar row off the category's own
// `group` field (readCategories) rather than a DOM lookup — no need to wait for the spans to mount.
function inkVarFor(categories) {
  const byKey = Object.fromEntries(
    categories.map((cat) => [cat.key, GROUP_INK_VAR[cat.group]]),
  );
  return (key) => byKey[key];
}

// The bars' tips read compact past 1000 (sig-digit-numbers task, charts.js's own default), so each
// row hands the builder the exact net as its `detail` — the tooltip keeps the truth the tip rounds
// off. `exactDetail` answers null below 1000, where the tip already IS the exact figure and a
// detail would print the same digits twice; `divergingBarRow` omits the attribute for a null
// detail, so the suppression needs no branch here.
function mountBars(buildDivergingBars, barsHost, panel, categories, motion) {
  const size = panel.clientWidth >= 720 ? "large" : "compact";
  const rows = categories.map((cat) => ({
    key: cat.key,
    label: categoryLabel(cat.key),
    value: cat.total,
    detail: exactDetail(cat.total),
  }));
  barsHost.appendChild(
    buildDivergingBars(rows, {
      icon: categoryIcon,
      iconOnly: true,
      iconOnlyGutterPx,
      iconOnlyIconHeightPx,
      inkVar: inkVarFor(categories),
      size,
      motion,
    }),
  );
}

// A group key now IS a round-type key, just lowercased (per-suit-farbspiel-entries — the
// "grouped donut dimension" deviation charts/CLAUDE.md once recorded no longer exists), so the
// donut's icon is charts.js's own roundTypeChartIcon directly — no panel-side group-icon mapping.
function mountDonut(charts, donutHost, categories, motion) {
  const groups = foldGroups(categories);
  donutHost.appendChild(
    charts.buildDonut(groups, {
      icon: (key, size) => charts.roundTypeChartIcon(key.toUpperCase(), size),
      inkVar: (key) => GROUP_INK_VAR[key],
      heroTotal: groups.reduce((sum, g) => sum + g.value, 0),
      heroLabel: t("runStatsRoundsPlayed"),
      size: "compact",
      motion,
    }),
  );
}

/** Mounts the bars + donut off the fragment's `sp-chart-data` spans (skip-when-populated — safe
 *  on a re-enhance) — panel-side gutter mass calibration lives here, not in charts.js, which stays
 *  round-type-agnostic. `motion` plays once per consumer visit,
 *  tracked on `root` (survives a same-row leaderboard re-expand's fragment rebuild — a fresh
 *  overlay `content` div, by contrast, is always a genuine first visit). */
async function mountCharts(root) {
  const panel = root.querySelector(".stats-panel-v2");
  const barsHost = panel?.querySelector(".sp-chart-bars");
  const donutHost = panel?.querySelector(".sp-chart-donut");
  if (barsHost == null || donutHost == null || barsHost.firstChild != null)
    return;
  const categories = readCategories(panel);
  if (categories.length === 0) return;
  const motion = !root._spChartsMotionPlayed;
  root._spChartsMotionPlayed = true;
  const charts = await loadCharts();
  // Re-check after the await: a second enhance() on the same root can interleave here (the guard
  // above ran before the module resolved), and a double mount would stack two charts per host.
  if (barsHost.firstChild != null) return;
  mountBars(charts.buildDivergingBars, barsHost, panel, categories, motion);
  mountDonut(charts, donutHost, categories, motion);
}

// --- Interaction (design § Interaction contract) ------------------------------------------------

// A bar row is a category mark; every other `[data-key]` mark (a donut path or its outside-ring
// icon host) is a group mark — charts.js stamps `dataset.key` on all three (charts.js's key-
// stamping contract).
function isCategoryMark(mark) {
  return mark.classList.contains("chart-diverging-bar-row");
}

function categoriesInGroup(panel, groupKey) {
  const keys = new Set();
  for (const span of panel.querySelectorAll(".sp-chart-data")) {
    if (span.dataset.group === groupKey) keys.add(span.dataset.key);
  }
  return keys;
}

function categoryGroup(panel, categoryKey) {
  for (const span of panel.querySelectorAll(".sp-chart-data")) {
    if (span.dataset.key === categoryKey) return span.dataset.group;
  }
  return categoryKey;
}

// Matching sets (design § Interaction contract): a group mark (donut) lights its own segment plus
// every bar row in that group; a category mark (a bar row) lights only itself plus its own group's
// segment — the sibling same-group bar rows dim, since the block is showing the hovered category,
// not the group.
function litSet(panel, key, isCategory) {
  return isCategory ? new Set([key]) : categoriesInGroup(panel, key);
}

function litGroup(panel, key, isCategory) {
  return isCategory ? categoryGroup(panel, key) : key;
}

function showVariant(panel, key) {
  for (const variant of panel.querySelectorAll(".sp-context-variant")) {
    variant.hidden = variant.dataset.variant !== key;
  }
}

function setActive(panel, active) {
  panel
    .querySelector(".chart-diverging-bars")
    ?.classList.toggle("sp-xh-active", active);
  panel.querySelector(".chart-donut")?.classList.toggle("sp-xh-active", active);
}

function engage(panel, key, isCategory) {
  setActive(panel, true);
  const lit = litSet(panel, key, isCategory);
  const litGroupKey = litGroup(panel, key, isCategory);
  for (const row of panel.querySelectorAll(
    ".chart-diverging-bar-row[data-key]",
  )) {
    row.classList.toggle("sp-xh-dim", !lit.has(row.dataset.key));
  }
  for (const mark of panel.querySelectorAll(
    "path[data-key], .chart-icon-host[data-key]",
  )) {
    mark.classList.toggle("sp-xh-dim", mark.dataset.key !== litGroupKey);
  }
  showVariant(panel, key);
}

function rest(panel) {
  setActive(panel, false);
  for (const mark of panel.querySelectorAll(".sp-xh-dim"))
    mark.classList.remove("sp-xh-dim");
  showVariant(panel, "rest");
}

function handleHoverIn(panel, pin, e) {
  if (pin.key != null) return; // fully suppressed while pinned — no preview
  const mark = e.target.closest("[data-key]");
  if (mark == null) return;
  engage(panel, mark.dataset.key, isCategoryMark(mark));
}

function handleHoverOut(panel, pin, e) {
  if (pin.key != null) return; // fully suppressed while pinned — no rest-restore
  const from = e.target.closest("[data-key]");
  if (from == null) return;
  const to = e.relatedTarget?.closest?.("[data-key]");
  if (from === to) return; // intra-row move (icon -> track -> tip) — no flicker
  rest(panel);
}

function handleClick(panel, pin, e) {
  const mark = e.target.closest("[data-key]");
  const key = mark?.dataset.key ?? null;
  if (key != null && key === pin.key) {
    pin.key = null; // same mark again — unpin
    rest(panel);
  } else if (key != null) {
    pin.key = key; // a different mark — (re-)pin to it
    engage(panel, key, isCategoryMark(mark));
  } else if (pin.key != null) {
    pin.key = null; // a tap outside the charts — unpin
    rest(panel);
  }
}

/** Wires the hover/pin cross-highlight (design § Interaction contract) — one delegated
 *  pointerover/pointerout/click set on the panel root, bound once (`data-sp-wired` guards a
 *  redundant re-wire on an already-live panel instance; a genuine re-fetch always parses a fresh
 *  `.stats-panel-v2` element, which starts unwired again — no leaked listener). `pin` is a
 *  closure-shared mutable cell, not module state: each mounted panel gets its own, reset by
 *  construction on every fresh mount. */
function wireInteraction(root) {
  const panel = root.querySelector(".stats-panel-v2");
  if (panel == null || panel.dataset.spWired != null) return;
  panel.dataset.spWired = "true";
  const pin = { key: null };
  panel.addEventListener("pointerover", (e) => handleHoverIn(panel, pin, e));
  panel.addEventListener("pointerout", (e) => handleHoverOut(panel, pin, e));
  panel.addEventListener("click", (e) => handleClick(panel, pin, e));
}

/** Enhances one mounted stats-panel-v2 fragment: fills every icon slot, mounts the bars/donut off
 *  the chart-data spans, and wires the cross-highlight. Safe to call again on a fragment re-fetch
 *  (every sub-step is independently idempotent).
 *
 *  Returns a promise that settles once the charts have mounted — the charts module loads on demand
 *  (see loadCharts above), so the panel reaches its final height asynchronously. A caller that
 *  measures the mounted fragment must await this; the leaderboard's max-height transition
 *  (leaderboard-expansion.js#expandDetail) is exactly that caller.
 *
 *  The net chart (leaderboard-row-daily-net-columns) mounts alongside the triad rather than inside
 *  `mountCharts`, and the two are structurally independent: `mountCharts` bails on a missing or
 *  already-populated bars host and on a category-less panel, none of which says anything about
 *  whether this fragment carries a net series. Both are awaited together so the returned promise
 *  still means "the panel has reached its final height".
 *
 *  `fillKpiIcons` is the sibling leaf's (stats-kpi-icons.js): the KPI strip is a shared block now
 *  (stats-panel-unification), so filling it belongs to the module BOTH surfaces already call on
 *  their mounted root — this one — rather than to /games' own enhancer, which the leaderboard row
 *  never runs. Synchronous like fillIcons, and idempotent the same way.
 *
 *  Only the net chart's rejection is absorbed, and deliberately not via `allSettled`. Both mounts
 *  load a chunk on demand, so both can reject on a stale-client miss (`stale-client.js` exists for
 *  that class) — but the triad IS the panel, and its failure propagating is what lets
 *  `expandDetail`'s catch drop the cached fragment so the next expand refetches. The net chart is
 *  one block inside that panel: letting it fail the pair would newly cost the row its measured
 *  height over a secondary mark, while `allSettled` would buy that back only by swallowing the
 *  triad's rejection too and taking the retry with it. Warn-and-continue matches this project's
 *  posture for a failed visual flourish (js/CLAUDE.md § Error handling). */
export function enhance(root) {
  fillIcons(root);
  fillKpiIcons(root);
  wireInteraction(root);
  return Promise.all([
    mountCharts(root),
    mountNetChart(root).catch((e) =>
      console.warn("[stats-panel] net chart failed to mount:", e.message),
    ),
  ]);
}
